import type { DamageType } from "../damageType";
import { Direction, directionDelta } from "../direction";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../effects/movementEffect";
import type {
	OnAddToBoard,
	PointsToDirection,
	ProjectileHittable,
	RemoteDoor,
	SomethingWithState,
} from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { PhysicalThingWithHp } from "../physicalThingWithHp";
import { deserializeXY, serializeXY } from "../serialization";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export enum SlidingDoorState {
	OPEN = "OPEN",
	CLOSED = "CLOSED",
}

export class SlidingDoor
	extends PhysicalThingWithHp
	implements PointsToDirection, ProjectileHittable, SomethingWithState, RemoteDoor, OnAddToBoard
{
	static key = "SlidingDoor";
	private direction: Direction;
	private originalXY: XY;
	private state: SlidingDoorState = SlidingDoorState.CLOSED;
	readonly doorId: string | null;

	constructor(xy: XY, direction: Direction, doorId?: string) {
		super(xy, true);
		this.direction = direction;
		this.originalXY = xy;
		this.doorId = doorId ?? null;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const dir = (props?.direction as Direction | undefined) ?? Direction.E;
		const door = new SlidingDoor(xy, dir, props?.group as string | undefined);
		board.putThing(door);
		return [door];
	}

	getZOrder(): number {
		return ZOrder.SCENARIO - 1;
	}

	onAddToBoard(): void {
		this.originalXY = this.xy;
	}

	override usesMinSortYWhileMoving(): boolean {
		return true;
	}

	getDirection(): Direction | null {
		return this.direction;
	}

	setDirection(dir: Direction): void {
		this.direction = dir;
	}

	getState(): SlidingDoorState {
		return this.state;
	}

	getOriginalXY(): XY {
		return this.originalXY;
	}

	open(): void {
		if (this.state === SlidingDoorState.OPEN) return;
		const dest = this.originalXY.add(directionDelta(this.direction));
		if (this.board && !this.board.isInBounds(dest)) return;
		const origin = this.xy;
		this.state = SlidingDoorState.OPEN;
		this.blocksMovement = false;
		this.slideTo(dest);
		if (this.board) {
			this.board.addEffect(new MovementEffect(this, origin, dest, this.getOpenAnimationTime()));
			this.board.addNonPhysicalThing(new Sound(SoundId.SLIDING_DOOR_OPEN, this.xy));
		}
	}

	protected getOpenAnimationTime(): number {
		return STANDARD_MOVEMENT_TIME * 5;
	}

	close(): void {
		if (this.state === SlidingDoorState.CLOSED) return;
		if (this.board && !this.board.isPassable(this.originalXY)) return;
		const origin = this.xy;
		const dest = this.originalXY;
		this.state = SlidingDoorState.CLOSED;
		this.blocksMovement = true;
		this.slideTo(dest);
		if (this.board) {
			this.board.addEffect(new MovementEffect(this, origin, dest));
			this.board.addNonPhysicalThing(new Sound(SoundId.SLIDING_DOOR_OPEN, this.xy));
		}
	}

	// Raw xy assignment would strand the door in its old cell — the board only auto-reindexes self-moves.
	private slideTo(dest: XY): void {
		if (this.board) {
			this.board.moveThing(this, dest);
		} else {
			this.xy = dest;
		}
	}

	onProjectileHit(_projectile: unknown): void {
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	isHittable(): boolean {
		return this.state === SlidingDoorState.CLOSED;
	}

	protected isDestructible(): boolean {
		return false;
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		return this.isDestructible() ? super.applyDamage(type, amount, source) : 0;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			direction: this.direction,
			originalXY: serializeXY(this.originalXY),
			state: this.state,
			doorId: this.doorId,
		};
	}

	protected restoreDoorFields(data: any): void {
		this.restoreBaseFields(data);
		this.originalXY = data.originalXY ? deserializeXY(data.originalXY) : this.xy;
		this.state = data.state ?? SlidingDoorState.CLOSED;
		if (this.state === SlidingDoorState.OPEN) {
			this.blocksMovement = false;
		}
	}

	static deserialize(data: any): SlidingDoor {
		const d = new SlidingDoor(deserializeXY(data.xy), data.direction, data.doorId ?? undefined);
		d.restoreDoorFields(data);
		return d;
	}
}
